import { useState } from 'react';
import { api } from '../../api';
import { useAuth } from '../../App';
import { useIsMobile } from '../../utils';
import { ResellerCeilingEditor } from './ResellerCeilingEditor';

export function EditClientModal({ client, plans, onClose, onSaved }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({
    name:             client.name || '',
    cuit:             client.cuit || '',
    contact_name:     client.contact_name || '',
    contact_email:    client.contact_email || '',
    contact_phone:    client.contact_phone || '',
    city:             client.city || '',
    address:          client.address || '',
    plan_id:          client.plan?.id || '',
    storage_quota_gb: client.storage_quota_gb ?? null,
    is_reseller:      !!client.is_reseller,
    client_can_manage_jobs: client.client_can_manage_jobs !== false,
    reseller_bucket_prefix:    client.reseller_bucket_prefix || '',
    reseller_discount_pct:     client.reseller_discount_pct ?? '',
    reseller_storage_quota_gb: client.reseller_storage_quota_gb ?? '',
  });
  // Un reseller sin can_manage_billing no puede cambiar plan ni cuota (#198 parte 2).
  const { me, isAdmin, isOperator, isReseller, resellerPerms, perms } = useAuth();
  // El plan y la cuota de la propia empresa los gestiona OpenBackup (el backend responde 403)
  const ownCompany = isReseller && !!me?.client_id && client.id === me.client_id;
  const lockBilling = (isReseller && !resellerPerms?.can_manage_billing) || ownCompany;
  // #210 etapa 2: «El cliente puede gestionar sus jobs» lo editan el admin, el operador con can_edit_clients y el reseller dueño; no aplica a la empresa reseller
  const canEditJobsSetting = (isAdmin || isReseller || (isOperator && !!perms?.can_edit_clients)) && !client.internal && !client.is_reseller;
  // El plan es obligatorio salvo que el campo esté deshabilitado para quien edita
  // (no se manda) o el cliente sea el interno, que no tiene plan ni facturación.
  // #210: una empresa reseller (marcada en el formulario) puede quedar sin plan.
  const planRequired = !lockBilling && !client.internal && !form.is_reseller;
  const BILLING_LOCK_TEXT = ownCompany ? 'Los gestiona OpenBackup.' : 'Sólo el administrador, o un reseller con permiso de facturación, puede cambiarlo.';
  const [saving, setSaving]       = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]         = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSubmitted(true);
    if (!form.name?.trim()) {
      setError('La razón social es obligatoria');
      return;
    }
    if (!form.contact_name?.trim()) {
      setError('El nombre de contacto es obligatorio');
      return;
    }
    if (!form.contact_email?.trim()) {
      setError('El email de contacto es obligatorio');
      return;
    }
    if (planRequired && !form.plan_id) {
      setError('Debe seleccionar un plan');
      return;
    }
    setError('');
    setSaving(true);
    try {
      // Plan y cuota sólo viajan si cambiaron: el backend los rechaza para un
      // reseller sin facturación, y la cuota que se muestra es la efectiva
      // (la del plan), no un override guardado.
      const payload = { ...form, storage_quota_gb: form.storage_quota_gb || null };
      if (form.plan_id === (client.plan?.id || '')) delete payload.plan_id;
      else if (!form.plan_id) payload.plan_id = null;   // quitar el plan (sólo una empresa reseller)
      if (payload.storage_quota_gb === (client.storage_quota_gb ?? null)) delete payload.storage_quota_gb;
      // #210: «Es reseller» y sus datos son del admin; los rechazos del backend (tiene reseller,
      // usuarios client, clientes, usuarios reseller) salen como error
      if (isAdmin && !client.internal && form.is_reseller) {
        payload.reseller_bucket_prefix = form.reseller_bucket_prefix.trim() || null;
        payload.reseller_discount_pct = form.reseller_discount_pct === '' ? null : parseFloat(form.reseller_discount_pct);
        payload.reseller_storage_quota_gb = form.reseller_storage_quota_gb === '' ? null : parseFloat(form.reseller_storage_quota_gb);
      } else {
        delete payload.reseller_bucket_prefix;
        delete payload.reseller_discount_pct;
        delete payload.reseller_storage_quota_gb;
      }
      if (form.is_reseller === !!client.is_reseller) delete payload.is_reseller;
      if (!canEditJobsSetting || form.client_can_manage_jobs === (client.client_can_manage_jobs !== false)) delete payload.client_can_manage_jobs;
      await api.updateClient(client.id, payload);
      onSaved();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  // [label, key, type, required]
  const fields = [
    ['Razón Social', 'name',          'text',  true],
    ['CUIT',         'cuit',          'text',  false],
    ['Contacto',     'contact_name',  'text',  true],
    ['Email',        'contact_email', 'email', true],
    ['Teléfono',     'contact_phone', 'text',  false],
    ['Ciudad',       'city',          'text',  false],
    ['Dirección',    'address',       'text',  false],
  ];

  const inputBorder = (key, required) =>
    submitted && required && !form[key]?.trim()
      ? '1px solid var(--red)'
      : '1px solid var(--border)';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        zIndex: 200, display: 'flex', alignItems: 'center',
        justifyContent: 'center', backdropFilter: 'blur(4px)',
        padding: '16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* HEADER */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1 }}>
            EDITAR CLIENTE
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--muted)',
                     cursor: 'pointer', fontSize: 20, lineHeight: 1 }}
          >×</button>
        </div>

        {/* BODY */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {error && (
            <div style={{ color: 'var(--red)', fontFamily: 'var(--mono)',
                          fontSize: 12, marginBottom: 16,
                          padding: '10px 14px',
                          border: '1px solid var(--red)',
                          background: 'rgba(255,77,109,0.08)' }}>
              {error}
            </div>
          )}

          {fields.map(([label, key, type, required]) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
                {label}{required && <span style={{ color: 'var(--red)', marginLeft: 2 }}>*</span>}
              </label>
              <input
                type={type}
                value={form[key]}
                onChange={e => set(key, e.target.value)}
                style={{ width: '100%', background: 'var(--bg)', border: inputBorder(key, required), color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          ))}

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
              Plan {planRequired && <span style={{ color: 'var(--red)' }}>*</span>}
            </label>
            <select
              value={form.plan_id}
              onChange={e => set('plan_id', e.target.value)}
              disabled={lockBilling}
              title={lockBilling ? BILLING_LOCK_TEXT : undefined}
              style={{ width: '100%', background: 'var(--bg)', border: submitted && planRequired && !form.plan_id ? '1px solid var(--red)' : '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer', boxSizing: 'border-box' }}
            >
              {form.is_reseller && !client.internal
                ? <option value="">Sin plan (sin infraestructura propia)</option>
                : !form.plan_id && <option value="">Sin plan</option>}
              {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {lockBilling && (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, fontFamily: 'var(--mono)' }}>{BILLING_LOCK_TEXT}</div>
            )}
          </div>

          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Cuota de Storage (GB)</label>
            <input
              type="number"
              value={form.storage_quota_gb ?? ''}
              onChange={e => set('storage_quota_gb', e.target.value ? parseInt(e.target.value) : null)}
              disabled={lockBilling}
              title={lockBilling ? BILLING_LOCK_TEXT : undefined}
              placeholder={`${client.plan?.included_storage_gb || 0} (del plan)`}
              style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, fontFamily: 'var(--mono)' }}>
              Dejar vacío para usar el valor del plan ({client.plan?.included_storage_gb || '—'} GB).
            </div>
          </div>

          {canEditJobsSetting && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.client_can_manage_jobs}
                onChange={e => set('client_can_manage_jobs', e.target.checked)}
                style={{ marginTop: 2, accentColor: 'var(--accent)' }} />
              <span style={{ fontSize: 12, fontFamily: 'var(--mono)' }}>
                El cliente puede gestionar sus jobs
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                  Si lo desactivás, el usuario del cliente ve sus jobs y el historial, los ejecuta a mano y restaura, pero no los crea, edita, borra ni pausa.
                </span>
              </span>
            </label>
          )}

          {isAdmin && !client.internal && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_reseller}
                onChange={e => set('is_reseller', e.target.checked)}
                style={{ marginTop: 2, accentColor: 'var(--accent)' }} />
              <span style={{ fontSize: 12, fontFamily: 'var(--mono)' }}>
                Es reseller
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                  Empresa reseller: sus clientes y usuarios reseller cuelgan de este cliente. Para marcarlo no puede tener reseller ni usuarios de cliente; para desmarcarlo, ni clientes ni usuarios reseller.
                </span>
              </span>
            </label>
          )}

          {isAdmin && !client.internal && form.is_reseller && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12, marginTop: 14 }}>
              {[['Prefijo de buckets', 'reseller_bucket_prefix', 'text', 'ej. acme'],
                ['Descuento (%)', 'reseller_discount_pct', 'number', '0'],
                ['Cuota del reseller (GB)', 'reseller_storage_quota_gb', 'number', 'sin tope']].map(([label, key, type, ph]) => (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>{label}</label>
                  <input type={type} min={type === 'number' ? 0 : undefined} placeholder={ph} value={form[key]}
                    onChange={e => set(key, key === 'reseller_bucket_prefix' ? e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') : e.target.value)}
                    style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>
          )}

          {isAdmin && !client.internal && client.is_reseller && form.is_reseller && (
            <ResellerCeilingEditor clientId={client.id} />
          )}

          <div style={{
            fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)',
            marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--border)',
          }}>
            <span style={{ color: 'var(--red)' }}>*</span> Campos obligatorios
          </div>
        </div>

        {/* FOOTER */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: '1px solid var(--border)',
                     color: 'var(--text)', padding: '8px 16px', fontSize: 12,
                     fontFamily: 'var(--mono)', cursor: 'pointer' }}
          >
            CANCELAR
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ background: 'var(--accent)', border: '1px solid var(--accent)',
                     color: 'var(--bg)', padding: '8px 16px', fontSize: 12,
                     fontFamily: 'var(--mono)', cursor: saving ? 'not-allowed' : 'pointer',
                     fontWeight: 700, letterSpacing: 1 }}
          >
            {saving ? 'GUARDANDO...' : 'GUARDAR →'}
          </button>
        </div>
      </div>
    </div>
  );
}
